/**
 * Share overlay: centered glass card over the player with copy-link and
 * social share targets (X, Facebook, WhatsApp, Telegram, Email + native
 * share where supported). Opened from the settings menu's Share row.
 * All dynamic strings render via textContent/encodeURIComponent.
 */

const SVG = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"'

const ICONS: Record<string, string> = {
  x: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.4l-5.8-7.59-6.64 7.59H.47l8.6-9.83L0 1.15h7.59l5.24 6.93zm-1.29 19.5h2.04L6.49 3.24H4.3z"/></svg>`,
  facebook: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.09 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.1 24 12.07z"/></svg>`,
  whatsapp: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.21 5.1 4.5.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 9.89 9.89c0 5.45-4.44 9.88-9.9 9.88zm8.42-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.94L.06 24l6.31-1.66a11.88 11.88 0 0 0 5.68 1.45c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.47-8.4z"/></svg>`,
  telegram: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.94 0C5.35 0 0 5.35 0 11.94c0 6.6 5.35 11.94 11.94 11.94 6.6 0 11.94-5.35 11.94-11.94C23.88 5.35 18.54 0 11.94 0zm5.85 8.16-1.96 9.25c-.15.66-.54.82-1.1.51l-3.03-2.24-1.46 1.41c-.16.16-.3.3-.61.3l.22-3.1 5.63-5.09c.24-.22-.05-.34-.38-.12l-6.96 4.38-3-1.16c-.65-.2-.66-.65.14-.96l11.72-4.52c.54-.2 1.02.13.79 1.34z"/></svg>`,
  email: `<svg ${SVG}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
  more: `<svg ${SVG}><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>`,
  close: `<svg ${SVG}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  link: `<svg ${SVG}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
  check: `<svg ${SVG}><polyline points="20 6 9 17 4 12"/></svg>`,
}

export interface ShareContext {
  url: string
  title: string
}

let activeCleanup: (() => void) | null = null

export function openShareOverlay(playerEl: HTMLElement, ctx: ShareContext): void {
  activeCleanup?.() // only one overlay at a time

  const backdrop = document.createElement('div')
  backdrop.className = 'lp-share-backdrop'

  const card = document.createElement('div')
  card.className = 'lp-share-card'

  // header
  const header = document.createElement('div')
  header.className = 'lp-share-header'
  const heading = document.createElement('strong')
  heading.textContent = 'Share'
  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = 'lp-share-close'
  closeBtn.setAttribute('aria-label', 'Close')
  closeBtn.innerHTML = ICONS.close // static markup
  header.append(heading, closeBtn)

  // social targets
  const encodedUrl = encodeURIComponent(ctx.url)
  const encodedTitle = encodeURIComponent(ctx.title || 'Watch this stream')
  const targets: Array<{ key: string; label: string; href: string }> = [
    { key: 'x', label: 'X', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}` },
    { key: 'facebook', label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { key: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}` },
    { key: 'telegram', label: 'Telegram', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}` },
    { key: 'email', label: 'Email', href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}` },
  ]

  const socials = document.createElement('div')
  socials.className = 'lp-share-socials'
  for (const t of targets) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'lp-share-social'
    const circle = document.createElement('span')
    circle.className = 'lp-share-social-icon'
    circle.innerHTML = ICONS[t.key] // static markup
    const label = document.createElement('span')
    label.textContent = t.label
    btn.append(circle, label)
    btn.addEventListener('click', () => {
      window.open(t.href, '_blank', 'noopener,noreferrer,width=640,height=540')
    })
    socials.appendChild(btn)
  }

  // native share (mobile/safari)
  if (typeof navigator.share === 'function') {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'lp-share-social'
    const circle = document.createElement('span')
    circle.className = 'lp-share-social-icon'
    circle.innerHTML = ICONS.more // static markup
    const label = document.createElement('span')
    label.textContent = 'More'
    btn.append(circle, label)
    btn.addEventListener('click', () => {
      void navigator.share({ title: ctx.title, url: ctx.url }).catch(() => undefined)
    })
    socials.appendChild(btn)
  }

  // copy-link row
  const linkRow = document.createElement('div')
  linkRow.className = 'lp-share-link'
  const linkIcon = document.createElement('span')
  linkIcon.className = 'lp-share-link-icon'
  linkIcon.innerHTML = ICONS.link // static markup
  const linkText = document.createElement('span')
  linkText.className = 'lp-share-link-url'
  linkText.textContent = ctx.url
  linkText.title = ctx.url
  const copyBtn = document.createElement('button')
  copyBtn.type = 'button'
  copyBtn.className = 'lp-share-copy'
  copyBtn.textContent = 'Copy'
  linkRow.append(linkIcon, linkText, copyBtn)

  let copyResetTimer: ReturnType<typeof setTimeout> | undefined
  copyBtn.addEventListener('click', async () => {
    let ok = false
    try {
      await navigator.clipboard.writeText(ctx.url)
      ok = true
    } catch {
      // fallback: hidden textarea
      try {
        const ta = document.createElement('textarea')
        ta.value = ctx.url
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        card.appendChild(ta)
        ta.select()
        ok = document.execCommand('copy')
        ta.remove()
      } catch {
        ok = false
      }
    }
    copyBtn.textContent = ok ? 'Copied!' : 'Copy failed'
    copyBtn.classList.toggle('copied', ok)
    if (copyResetTimer) clearTimeout(copyResetTimer)
    copyResetTimer = setTimeout(() => {
      copyBtn.textContent = 'Copy'
      copyBtn.classList.remove('copied')
    }, 1600)
  })

  card.append(header, socials, linkRow)
  backdrop.appendChild(card)
  playerEl.appendChild(backdrop)

  // open animation next frame
  requestAnimationFrame(() => backdrop.classList.add('open'))

  const close = (): void => {
    activeCleanup = null
    if (copyResetTimer) clearTimeout(copyResetTimer)
    document.removeEventListener('keydown', onKey, true)
    backdrop.classList.remove('open')
    setTimeout(() => backdrop.remove(), 220)
  }

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close()
  }

  closeBtn.addEventListener('click', close)
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) close()
  })
  card.addEventListener('click', (ev) => ev.stopPropagation())
  document.addEventListener('keydown', onKey, true)

  activeCleanup = close
}
